import { compose, size, filter, isNil, reject, sortBy, map, reverse, get, compact } from 'lodash/fp';
import { createSelector } from 'reselect';
import { getStringProp } from '@mews/fp';
import { DateTimeService } from '@mews/framework';
import { getFullName, mewsEnterprise, getImageUrlOrNull } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getEnterprise, getEnterprises } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getScopes } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { MODULE_NAME } from '../constants';
import parentSelector from '../../selector';
import { isSeen } from '../utils/isSeen';
import { getImageBaseUrl } from '../../app/reducers/configuration';
import getUnreadCount from '../utils/getUnreadCount';

const moduleSelector = createSelector(parentSelector, (state) => state[MODULE_NAME]);

const getNotifications = createSelector(moduleSelector, (state) => state.notifications);

const countUnseen = compose(size, reject(isSeen));

const isSystemNotification = compose(isNil, get('message'));

const filterProfileNotifications = (
    timezone: $TSFixMe,
    notifications: $TSFixMe,
    imageBaseUrl: $TSFixMe,
    profileId: $TSFixMe
) =>
    compose(
        map((notification) => ({
            // @ts-expect-error $TSFixMeError: Spread types may only be created from object types.
            ...notification,
            createdDateTime: DateTimeService.fromUtcString((notification as $TSFixMe).createdUtc, timezone),
            authorName: getFullName(get('author', notification)),
            authorImageUrl: getImageUrlOrNull(imageBaseUrl)({ imageId: get('author.imageId', notification) }),
        })),
        reverse,
        sortBy('createdUtc'),
        filter({ profileId })
    )(notifications);

export const getKnownNotificationIds = createSelector(moduleSelector, (state) => state.knownNotifications);

export const getSystemNotifications = createSelector(getNotifications, (notifications) =>
    filter(isSystemNotification)(notifications)
);

export const getMessageNotifications = createSelector(getNotifications, (notifications) =>
    reject(isSystemNotification)(notifications)
);

export const getProfileSystemNotifications = createSelector(
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getSystemNotifications,
    getImageBaseUrl,
    getStringProp('profileId'),
    filterProfileNotifications
);

export const getProfileMessageNotifications = createSelector(
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getMessageNotifications,
    getImageBaseUrl,
    getStringProp('profileId'),
    filterProfileNotifications
);

export const getUnreadSystemNotificationsCount = createSelector(getSystemNotifications, getUnreadCount);
export const getUnseenSystemNotificationsCount = createSelector(getSystemNotifications, countUnseen);

export const getUnreadMessageNotificationsCount = createSelector(getMessageNotifications, getUnreadCount);
export const getUnseenMessageNotificationsCount = createSelector(getMessageNotifications, countUnseen);

export const getSortedScopes = createSelector(
    getScopes,
    getEnterprises,
    getCurrentEnterpriseId,
    (scopes, enterprises, currentEnterpriseId) => {
        const { [currentEnterpriseId]: currentScope, ...others } = scopes;

        const otherScopes = compose(
            sortBy('enterpriseName'),
            map((scope) => {
                const { name } = enterprises[(scope as $TSFixMe).enterpriseId] || mewsEnterprise;

                // @ts-expect-error $TSFixMeError: Spread types may only be created from object types.
                return { ...scope, enterpriseName: name };
            })
        )(others);
        return compact([currentScope, ...otherScopes]);
    }
);

export const getMappedEnterprise = createSelector(
    getEnterprise,
    getImageBaseUrl,
    ({ logoImageId, ...other }, imageBaseUrl) => ({
        ...other,
        imageUrl: getImageUrlOrNull(imageBaseUrl)({ imageId: logoImageId }),
    })
);

export const getNotificationIds = createSelector(getNotifications, map('id'));
